import { c as _c } from "react/compiler-runtime";
import { useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Download, FileText, Loader2, ShieldAlert, Sparkles, Store, TrendingUp, UserCog, Wallet } from "lucide-react";
import { api, type ReportCatalogEntry } from "@/api";
import { useGenerateReport, useReportCatalog, useReportHistory } from "@/api/hooks/use-reports";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@osmix/ui/badge";
import { Input } from "@osmix/ui/input";
import { Button } from "@osmix/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { formatDateTime } from "@/lib/utils";
import { hasAdminPermission } from "@/lib/admin-permissions";
import { useAuth } from "@/store";

// Icon names travel from the backend catalog (`icon: "TrendingUp"`); resolve
// them against a small curated set so we ship only the icons we use. Anything
// unknown falls back to FileText rather than crashing the card.
const ICONS: Record<string, ComponentType<{
  className?: string;
}>> = {
  TrendingUp,
  Sparkles,
  Store,
  ShieldAlert,
  UserCog,
  Wallet,
  FileText
};
const PERIODS = [7, 14, 30, 90, 365] as const;

// Open the self-contained report HTML in a fresh window and trigger the print
// dialog — the browser's "Save as PDF" is the PDF export. Written content fires
// `onload`, but a guarded timeout covers browsers where it does not.
function printReport(html: string) {
  const w = window.open("", "_blank", "width=980,height=1200");
  if (!w) return;
  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    w.focus();
    w.print();
  };
  w.document.open();
  w.document.write(html);
  w.document.close();
  w.onload = doPrint;
  setTimeout(doPrint, 600);
}
type Viewer = {
  title: string;
  html: string;
  aiUsed: boolean;
};
export function ReportsPage() {
  const {
    t
  } = useTranslation();
  const {
    data: catalog,
    error: catalogError
  } = useReportCatalog();
  const {
    data: history
  } = useReportHistory();
  const generate = useGenerateReport();

  // По умолчанию — 12 месяцев: короткое окно на молодой площадке часто пустое,
  // и отчёт из нулей выглядит как заглушка. Широкое окно сразу показывает данные.
  const [days, setDays] = useState<number>(365);
  const [pendingType, setPendingType] = useState<string | null>(null);
  const [pendingAi, setPendingAi] = useState(false);
  const [viewer, setViewer] = useState<Viewer | null>(null);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const onGenerate = (entry: ReportCatalogEntry) => {
    setPendingType(entry.type);
    setPendingAi(entry.ai);
    generate.mutate({
      type: entry.type,
      days
    }, {
      onSuccess: report => {
        setViewer({
          title: report.title,
          html: report.html,
          aiUsed: report.aiUsed
        });
        setPendingType(null);
      },
      onError: () => setPendingType(null)
    });
  };

  // History rows only carry metadata; the HTML is re-fetched on click (the GET
  // route needs the bearer token, so it flows through the api layer as text).
  const onOpenHistory = async (id: string, title: string, aiUsed: boolean) => {
    setOpeningId(id);
    try {
      const html = await api.reports.getHtml(id);
      setViewer({
        title,
        html,
        aiUsed
      });
    } finally {
      setOpeningId(null);
    }
  };
  if (catalogError) return <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
				{catalogError.message}
			</div>;
  return <div className="flex flex-col gap-6">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div data-tour="reports-intro">
					<h1 className="text-2xl font-bold">{t("nav.reports")}</h1>
					<p className="text-sm text-muted-foreground">{t("reports.subtitle")}</p>
				</div>
				<ReportModelPicker />
				<label className="flex items-center gap-2 text-sm text-muted-foreground" data-tour="reports-period">
					<span className="whitespace-nowrap">{t("reports.period")}</span>
					<Select value={String(days)} onChange={event => setDays(Number(event.target.value))} className="w-40">
						{PERIODS.map(value => <option key={value} value={value}>
								{t("reports.daysN", {
              days: value
            })}
							</option>)}
					</Select>
				</label>
			</div>

			{catalog === undefined ? <p className="text-muted-foreground">{t("reports.loading")}</p> : <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-tour="reports-catalog">
					{catalog.map(entry_0 => {
        const Icon = ICONS[entry_0.icon] ?? FileText;
        const busy = generate.isPending && pendingType === entry_0.type;
        return <div key={entry_0.type} className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-xs">
								<div className="flex items-start justify-between gap-2">
									<div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
										<Icon className="size-5" />
									</div>
									{entry_0.ai && <Badge variant="secondary" className="gap-1">
											<Sparkles className="size-3" />
											{t("reports.aiBadge")}
										</Badge>}
								</div>
								<div className="min-w-0 flex-1">
									<h2 className="font-semibold">{entry_0.title}</h2>
									<p className="mt-1 text-sm text-muted-foreground">{entry_0.description}</p>
								</div>
								<Button className="mt-1 w-full" disabled={generate.isPending} onClick={() => onGenerate(entry_0)}>
									{busy && <Loader2 className="size-4 animate-spin" />}
									{busy ? entry_0.ai ? t("reports.generatingAi") : t("reports.generating") : t("reports.generate")}
								</Button>
							</div>;
      })}
				</div>}

			<section className="flex flex-col gap-2" data-tour="reports-history">
				<div className="flex items-baseline justify-between gap-3">
					<h2 className="text-lg font-semibold">{t("reports.history.title")}</h2>
					<p className="text-xs text-muted-foreground">{t("reports.history.retention")}</p>
				</div>
				{history === undefined ? <p className="text-muted-foreground">{t("reports.loading")}</p> : history.length === 0 ? <p className="text-sm text-muted-foreground">{t("reports.history.empty")}</p> : <Table>
						<TableHeader>
							<TableRow>
								<TableHead>{t("reports.history.col.title")}</TableHead>
								<TableHead>{t("reports.history.col.type")}</TableHead>
								<TableHead>{t("reports.history.col.ai")}</TableHead>
								<TableHead>{t("reports.history.col.date")}</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{history.map(row => <TableRow key={row.id} className="cursor-pointer" onClick={() => onOpenHistory(row.id, row.title, row.aiUsed)}>
									<TableCell className="font-medium">
										<span className="flex items-center gap-2">
											{openingId === row.id && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
											{row.title}
										</span>
									</TableCell>
									<TableCell className="font-mono text-xs text-muted-foreground">
										{row.type}
									</TableCell>
									<TableCell>
										{row.aiUsed ? <Badge variant="secondary" className="gap-1">
												<Sparkles className="size-3" />
												{t("reports.aiBadge")}
											</Badge> : <span className="text-muted-foreground">—</span>}
									</TableCell>
									<TableCell className="text-muted-foreground">
										{formatDateTime(row.createdAt)}
									</TableCell>
								</TableRow>)}
						</TableBody>
					</Table>}
			</section>

			<ReportViewer viewer={viewer} onClose={() => setViewer(null)} />
		</div>;
}
function ReportViewer(t0) {
  const $ = _c(33);
  const {
    viewer,
    onClose
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = viewer !== null;
  let t2;
  if ($[0] !== onClose) {
    t2 = open => !open && onClose();
    $[0] = onClose;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = viewer?.title;
  let t4;
  if ($[2] !== t || $[3] !== viewer?.aiUsed) {
    t4 = viewer?.aiUsed && <Badge variant="secondary" className="gap-1"><Sparkles className="size-3" />{t("reports.aiBadge")}</Badge>;
    $[2] = t;
    $[3] = viewer?.aiUsed;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <DialogTitle className="flex items-center gap-2">{t3}{t4}</DialogTitle>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("reports.viewerDescription");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <DialogDescription className="sr-only">{t6}</DialogDescription>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== viewer) {
    t8 = () => viewer && printReport(viewer.html);
    $[12] = viewer;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Download className="size-4" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t) {
    t10 = t("reports.downloadPdf");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t8) {
    t11 = <Button variant="outline" size="sm" onClick={t8}>{t9}{t10}</Button>;
    $[17] = t10;
    $[18] = t8;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t5 || $[22] !== t7) {
    t12 = <DialogHeader className="pr-8"><div className="flex flex-wrap items-center justify-between gap-3">{t5}{t7}{t11}</div></DialogHeader>;
    $[20] = t11;
    $[21] = t5;
    $[22] = t7;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== viewer) {
    t13 = viewer && <iframe title={viewer.title} srcDoc={viewer.html} className="h-[72vh] w-full rounded-md border bg-white" />;
    $[24] = viewer;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <DialogContent className="max-h-[92vh] gap-3 sm:max-w-5xl">{t12}{t13}</DialogContent>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t1 || $[30] !== t14 || $[31] !== t2) {
    t15 = <Dialog open={t1} onOpenChange={t2}>{t14}</Dialog>;
    $[29] = t1;
    $[30] = t14;
    $[31] = t2;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}

/**
 * Модель ИИ для отчётов.
 *
 * На стенде отчёты гоняются дешёвой моделью — это осознанно: генерация стоит
 * копейки и нужна ради проверки контура. На боевом контуре площадка выбирает
 * сильную, и ради этого не должно требоваться ни пересборки, ни разработчика.
 * Пустое поле означает «модель из конфигурации стенда».
 */
function ReportModelPicker() {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  // Модель ИИ — настройка интеграции: менять её может тот, кто управляет
  // интеграциями; остальные видят, какая модель стоит.
  const canEdit = hasAdminPermission(useAuth(s => s.user), "action.integrations.manage");
  const stored = useQuery({
    queryKey: ["reports", "model"],
    queryFn: () => api.reports.getModel()
  });
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const value = draft ?? stored.data ?? "";
  if (stored.isError) return null;
  const save = async () => {
    setSaving(true);
    try {
      await api.reports.setModel(value);
      await queryClient.invalidateQueries({
        queryKey: ["reports", "model"]
      });
      setDraft(null);
    } finally {
      setSaving(false);
    }
  };
  return <label className="flex max-w-full flex-wrap items-center gap-2 text-sm text-muted-foreground">
			<span className="whitespace-nowrap">
				{t("reports.model", {
        defaultValue: "Модель ИИ"
      })}
			</span>
			<Input className="h-9 w-64 max-w-full" placeholder={t("reports.modelDefault", {
      defaultValue: "по умолчанию для стенда"
    })} disabled={!canEdit} value={value} onChange={e => setDraft(e.target.value)} />
			{canEdit && <Button size="sm" variant="outline" disabled={saving || draft === null} onClick={() => void save()}>
					{t("common.save", {
        defaultValue: "Сохранить"
      })}
				</Button>}
		</label>;
}